import { useEffect, useMemo, useRef } from 'react';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import type { ScalarSpreadsheetConfigDataInterface } from 'models/interfaces/spreadsheet-types/core-types';
import { useCrossReferenceStore } from 'store/useCrossReferenceStore';
import { createCrossReferencePopupSheet, syncPopupSheetToEntries } from './createCrossReferencePopupSheet';
import type {
  CrossRefSheetColumn,
  CrossRefSheetInitialObject,
  CrossRefSheetTableData,
} from './createCrossReferencePopupSheet.types';
import type { CrossReferencePopupSheetProps } from './CrossReferencePopupSheet.props';
import { PopupPaper } from './CrossReferencePopupSheet.styles';

type PopupSheet = SpreadsheetConfig<CrossRefSheetInitialObject, CrossRefSheetColumn, CrossRefSheetTableData>;

// Self-contained popup spreadsheet for one cross-reffed cell.
//
// The popup builds and owns its own SpreadsheetConfig + useWorkbook so that
// auto-grow (adding a fresh trailing row when the user fills the previous
// trailing) is local to the popup. The host sheet is intentionally NOT in
// this workbook — it would otherwise have to be re-evaluated on every
// auto-grow tick, which churned the host's datasheet state and looked like
// the popup was stalling/closing.
//
// Save flow still works without host integration: reverseParser writes edits
// to the cross-ref store, and `distributeDirtyPayload` reads from there at
// save time.
const CrossReferencePopupSheet: React.FC<CrossReferencePopupSheetProps> = ({ cellKey, config, inheritedFormat }) => {
  // Watch the entry count for THIS cellKey so we can extend the rowConfig in
  // place when the user fills the trailing row. Counts only non-placeholder
  // rows — buildBlankState seeds an `isPlaceholder: true` row for multi_row
  // popups that should not contribute.
  const entryCount = useCrossReferenceStore(s => (s.states[cellKey]?.rows ?? []).filter(r => !r.isPlaceholder).length);

  // Build the SpreadsheetConfig once and keep the same instance across
  // re-renders. Mutating the same instance (via syncPopupSheetToEntries +
  // local re-eval) is what lets ScalarDatasheet preserve its internal state
  // through an auto-grow tick.
  const sheetRef = useRef<PopupSheet | null>(null);
  if (!sheetRef.current) {
    const initialRows = useCrossReferenceStore.getState().states[cellKey]?.rows ?? [];
    sheetRef.current = createCrossReferencePopupSheet({
      cellKey,
      initialRows,
      config,
      inheritedFormat,
      parentWidth: 0,
    });
  }

  // Stable single-element array so useWorkbook's effect doesn't rebuild every
  // render. We never swap the sheet instance; auto-grow mutates it in place.
  const spreadsheets = useMemo(
    () => [sheetRef.current as PopupSheet] as unknown as ScalarSpreadsheetConfigDataInterface[],
    []
  );
  const { workbook, onChange, evaluateSpreadsheets } = useWorkbook(spreadsheets);

  // When the user fills the trailing row, reverseParser bumps the store's
  // entry count. Mirror that into the live SpreadsheetConfig and re-evaluate
  // the popup's local workbook so a fresh trailing row is rendered. Because
  // the SpreadsheetConfig identity is preserved, ScalarDatasheet keeps its
  // selection / editing / focus state across this update.
  //
  // We skip the very first run (initial render): the SpreadsheetConfig was
  // already constructed from the same store state, and useWorkbook's mount
  // effect will evaluate it.
  const didInitialEvalRef = useRef(false);
  useEffect(() => {
    if (!didInitialEvalRef.current) {
      didInitialEvalRef.current = true;
      return;
    }
    const sheet = sheetRef.current;
    if (!sheet) {
      return;
    }
    const entries = (useCrossReferenceStore.getState().states[cellKey]?.rows ?? []).filter(r => !r.isPlaceholder);
    syncPopupSheetToEntries(sheet, entries, config);
    // Local re-eval: only this popup's sheet is in this workbook, so no host
    // sheet is touched.
    evaluateSpreadsheets([sheet] as unknown as ScalarSpreadsheetConfigDataInterface[]);
  }, [entryCount, cellKey, config, evaluateSpreadsheets]);

  const sheetFromWorkbook =
    (workbook as Record<string, PopupSheet> | undefined)?.[`xref_${cellKey}`] ?? sheetRef.current;
  const spreadsheetProps = useMemo(() => {
    if (!sheetFromWorkbook) {
      return null;
    }
    return {
      ...sheetFromWorkbook,
      data: sheetFromWorkbook.data ?? [],
      onChange,
      sheet: sheetFromWorkbook,
      workbook,
    };
  }, [sheetFromWorkbook, onChange, workbook]);

  if (!spreadsheetProps) {
    return null;
  }

  // Stop keyboard propagation: events from the popup's input would otherwise
  // bubble up to the host sheet's data-grid-container, where react-datasheet's
  // handleKey calls preventDefault on Backspace and arrow keys, blocking the
  // input's native cursor movement and delete behavior.
  const stopKeyboardPropagation = (event: React.KeyboardEvent) => {
    event.stopPropagation();
  };

  return (
    <PopupPaper
      elevation={4}
      data-testid={`cross-ref-popup-${cellKey}`}
      onKeyDown={stopKeyboardPropagation}
      onKeyUp={stopKeyboardPropagation}>
      {/* The spread carries our minimal popup shape; the legacy
          ScalarSpreadsheetProps type insists on every config field, but the
          host's generic onChange + sheet are what actually drive rendering.
          Cast to any to mirror how other call sites adapt the legacy typing. */}
      <ScalarSpreadsheet {...(spreadsheetProps as any)} />
    </PopupPaper>
  );
};

export default CrossReferencePopupSheet;
